// FO-147 (batch-28) — kosten toewijzen aan meerdere medewerkers per
// inkoopfactuurregel, automatische toewijzing (suggesties) en de
// organisatiebrede kosten-trends.
//
// Gedeelde Zod-schema's (API .parse/.safeParse + client-side prevalidatie,
// één bron — zie CLAUDE.md "Validatie & foutmeldingen") + DTO's. De verdeling
// wordt opgeslagen als AANDEEL-BEDRAG per medewerker (excl. btw, Decimal in de
// DB); percentage is puur invoerhulp in de UI. Kostensoort blijft PER REGEL.

import { z } from 'zod'
// Leaf-module (géén import uit ./inkoop — dat zou circulair zijn: inkoop.ts
// importeert de toewijzing-schema's hieronder).
import { idParam } from './id-validators'
import {
  TOEWIJZING_DOELTYPE,
  isKlantToewijzingDoeltype,
  isKostendragerDoeltype,
  type ToewijzingDoeltype,
} from './kostendrager'
import type { TrendGranulariteit, TrendPeriodeDTO } from './employee-kosten'

// ─── Bron + status (spiegelt de Prisma-enums) ────────────────────────────────

export const KOSTEN_TOEWIJZING_BRON = ['HANDMATIG', 'BESTELTRAJECT', 'AI', 'HERHALING'] as const
export type KostenToewijzingBron = (typeof KOSTEN_TOEWIJZING_BRON)[number]

export const KOSTEN_TOEWIJZING_BRON_LABELS: Record<KostenToewijzingBron, string> = {
  HANDMATIG: 'Handmatig',
  BESTELTRAJECT: 'Besteltraject',
  AI: 'AI-herkenning',
  HERHALING: 'Herhaling',
}

export const KOSTEN_TOEWIJZING_STATUS = ['SUGGESTIE', 'DEFINITIEF'] as const
export type KostenToewijzingStatus = (typeof KOSTEN_TOEWIJZING_STATUS)[number]

export const KOSTEN_TOEWIJZING_STATUS_LABELS: Record<KostenToewijzingStatus, string> = {
  SUGGESTIE: 'Voorgesteld',
  DEFINITIEF: 'Definitief',
}

// ─── Schrijfpad: toewijzingen-array per regel ────────────────────────────────

/**
 * Eén toewijzing (aandeel) in de invoer. FO-320: het doel is generiek — precies
 * één van medewerker / voertuig / kar-koffer (XOR), passend bij `doeltype`. Het
 * `doeltype` heeft een default (MEDEWERKER) zodat bestaande FO-147-payloads
 * (`{employeeId, bedrag}`) ongewijzigd geldig blijven (regressie).
 *
 * Blijft bewust een plain ZodObject (geen `.superRefine` hier) zodat consumenten
 * er `.extend({ bron })` op kunnen doen (inkoop.ts). De XOR-validatie draait via
 * {@link refineToewijzingDoel}, aangeroepen op regel-niveau
 * ({@link refineToewijzingenBinnenRegel}) en in de inline update-superRefine.
 */
export const inkoopRegelToewijzingInputSchema = z.object({
  doeltype: z.enum(TOEWIJZING_DOELTYPE).default('MEDEWERKER'),
  employeeId: z.string().min(1).nullish(),
  voertuigId: z.string().min(1).nullish(),
  gereedschapsDragerId: z.string().min(1).nullish(),
  // batch-65 inkoop-kosten (FO-483) — klant-toerekening: precies één van deze
  // drie bij doeltype KLANT / WERKBON / PROJECT (de klant van een werkbon of
  // project volgt uit die werkbon of dat project, nooit dubbel opgeslagen).
  clientId: z.string().min(1).nullish(),
  werkbonId: z.string().min(1).nullish(),
  projectId: z.string().min(1).nullish(),
  /** Aandeel-bedrag in euro's (excl. btw); negatief op creditregels. */
  bedrag: z.number().finite(),
  // FO-339 — optionele context-koppeling (de "andere kant", informatief). Niet
  // meegestuurd → de route leidt 'm af via de resolver op de factuurdatum;
  // expliciet `null` → bewust leeg (geen afleiding). Nooit gelijk aan de eigen
  // drager (route strippt zo'n waarde defensief).
  contextEmployeeId: z.string().min(1).nullish(),
  contextVoertuigId: z.string().min(1).nullish(),
})
export type InkoopRegelToewijzingInput = z.infer<typeof inkoopRegelToewijzingInputSchema>

/** De doel-velden van een toewijzing (FO-320 + batch-65 FO-483). */
export interface ToewijzingDoelVelden {
  doeltype: ToewijzingDoeltype
  employeeId?: string | null
  voertuigId?: string | null
  gereedschapsDragerId?: string | null
  clientId?: string | null
  werkbonId?: string | null
  projectId?: string | null
}

/**
 * XOR-validatie: precies één doel, passend bij het doeltype. Dezelfde regel
 * staat als CHECK-constraint in de database (`inkoop_toewijzing_doel_xor`,
 * migratie b65_inkoop_doorbelasten).
 */
export function refineToewijzingDoel(
  t: ToewijzingDoelVelden,
  ctx: z.RefinementCtx,
  pad: (string | number)[] = [],
): void {
  const doelen: [ToewijzingDoeltype, string | null | undefined][] = [
    ['MEDEWERKER', t.employeeId],
    ['VOERTUIG', t.voertuigId],
    ['DRAGER', t.gereedschapsDragerId],
    ['KLANT', t.clientId],
    ['WERKBON', t.werkbonId],
    ['PROJECT', t.projectId],
  ]
  const gevuld = doelen.filter(([, v]) => v != null && v !== '')
  if (gevuld.length !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Kies precies één doel: medewerker, voertuig, kar/koffer, klant, werkbon of project.',
      path: pad,
    })
    return
  }
  if (gevuld[0][0] !== t.doeltype) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Het gekozen doel past niet bij het doeltype.',
      path: [...pad, 'doeltype'],
    })
  }
}

/** De doel-id van een toewijzing (welke van de zes FK's gevuld is). */
export function toewijzingDoelId(t: ToewijzingDoelVelden): string | null {
  switch (t.doeltype) {
    case 'MEDEWERKER':
      return t.employeeId ?? null
    case 'VOERTUIG':
      return t.voertuigId ?? null
    case 'DRAGER':
      return t.gereedschapsDragerId ?? null
    case 'KLANT':
      return t.clientId ?? null
    case 'WERKBON':
      return t.werkbonId ?? null
    case 'PROJECT':
      return t.projectId ?? null
    default:
      return null
  }
}

/** Uniek-sleutel per (doeltype, doel) — dedup over gemengde doeltypen. */
export function toewijzingDoelKey(t: ToewijzingDoelVelden): string {
  return `${t.doeltype}:${toewijzingDoelId(t) ?? ''}`
}

/**
 * batch-65 inkoop-kosten (FO-483) — de doel-FK's als plat object, met alleen
 * het veld van het eigen doeltype gevuld (de rest null). Eén bron voor de
 * create-data op elk schrijfpad, zodat de DB-XOR-check nooit geraakt wordt door
 * een meegestuurd veld van een ander doeltype.
 */
export function toewijzingDoelKolommen(t: ToewijzingDoelVelden): {
  employeeId: string | null
  voertuigId: string | null
  gereedschapsDragerId: string | null
  clientId: string | null
  werkbonId: string | null
  projectId: string | null
} {
  return {
    employeeId: t.doeltype === 'MEDEWERKER' ? (t.employeeId ?? null) : null,
    voertuigId: t.doeltype === 'VOERTUIG' ? (t.voertuigId ?? null) : null,
    gereedschapsDragerId: t.doeltype === 'DRAGER' ? (t.gereedschapsDragerId ?? null) : null,
    clientId: t.doeltype === 'KLANT' ? (t.clientId ?? null) : null,
    werkbonId: t.doeltype === 'WERKBON' ? (t.werkbonId ?? null) : null,
    projectId: t.doeltype === 'PROJECT' ? (t.projectId ?? null) : null,
  }
}

/** Euro-number → integer centen (2 decimalen, bankiersvrij afgerond). */
const naarCentenLokaal = (n: number): number => Math.round(n * 100)

/**
 * Som-controle "som <= regelbedrag" (in absolute zin, tekens moeten meebewegen)
 * als gedeelde superRefine op het regel-niveau: veldfout op `toewijzingen` zodat
 * `issuesToFieldErrors` hem inline onder het blok zet. Een dubbel doel (zelfde
 * doeltype + doel) in één regel is ook een veldfout (de DB heeft @@unique per
 * doel-as).
 *
 * NB: dit is bewust een lokale centen-check (packages/types kan niet op
 * @streep/utils leunen — utils importeert types); de API gebruikt daarnaast de
 * utils-helper `toewijzingSomOverschrijdingCenten` op de Decimal-strings.
 */
export function refineToewijzingenBinnenRegel(
  regel: { totaal: number; toewijzingen?: InkoopRegelToewijzingInput[] | null },
  ctx: z.RefinementCtx,
  pad: (string | number)[] = ['toewijzingen'],
): void {
  const toewijzingen = regel.toewijzingen ?? []
  if (toewijzingen.length === 0) return

  const keys = new Set<string>()
  for (const [i, t] of toewijzingen.entries()) {
    refineToewijzingDoel(t, ctx, [...pad, i])
    const key = toewijzingDoelKey(t)
    if (keys.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Elk doel mag maar één keer in de verdeling staan.',
        path: pad,
      })
      break
    }
    keys.add(key)
  }

  // batch-65 (FO-483) — per dimensie: de interne dragers samen en de klant-
  // toerekening samen mogen elk niet boven het regelbedrag uitkomen. Een regel
  // mag op beide hangen (spec 183 §3.3); dat de twee samen meer zijn dan het
  // regelbedrag is een waarschuwing in de UI, geen fout.
  const regelCenten = naarCentenLokaal(regel.totaal)
  const { internCenten, klantCenten } = toewijzingDimensiesCenten(
    toewijzingen.map((t) => ({ doeltype: t.doeltype, bedragCenten: naarCentenLokaal(t.bedrag) })),
  )
  for (const [som, klant] of [
    [internCenten, false],
    [klantCenten, true],
  ] as const) {
    const over = toewijzingOverschrijdingCenten(regelCenten, som)
    if (over > 0) {
      const bedrag = (over / 100).toFixed(2).replace('.', ',')
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: klant
          ? `De toerekening aan klanten overschrijdt het regelbedrag met € ${bedrag} (excl. btw).`
          : `De verdeling overschrijdt het regelbedrag met € ${bedrag} (excl. btw).`,
        path: pad,
      })
    }
  }
}

/**
 * Overschrijding in centen van één dimensie tegen het regelbedrag: tekens moeten
 * meebewegen (een positieve toewijzing op een creditregel is per definitie een
 * overschrijding) en |som| <= |regel|. 0 = past.
 */
export function toewijzingOverschrijdingCenten(regelCenten: number, somCenten: number): number {
  if (somCenten === 0) return 0
  const tekenFout = (regelCenten >= 0 && somCenten < 0) || (regelCenten < 0 && somCenten > 0)
  if (tekenFout) return Math.abs(somCenten)
  const teVeel = regelCenten >= 0 ? somCenten > regelCenten : somCenten < regelCenten
  return teVeel ? Math.abs(somCenten - regelCenten) : 0
}

/**
 * batch-65 inkoop-kosten (FO-483) — de twee dimensies van een regel in centen:
 * interne dragers (medewerker/voertuig/kar) en klant-toerekening
 * (klant/werkbon/project). Pure helper voor API (werkvoorraad, dekking) en UI
 * (waarschuwing dubbel toerekenen).
 */
export function toewijzingDimensiesCenten(
  toewijzingen: ReadonlyArray<{ doeltype: ToewijzingDoeltype | string; bedragCenten: number }>,
): { internCenten: number; klantCenten: number } {
  let internCenten = 0
  let klantCenten = 0
  for (const t of toewijzingen) {
    if (isKlantToewijzingDoeltype(t.doeltype)) klantCenten += t.bedragCenten
    else if (isKostendragerDoeltype(t.doeltype)) internCenten += t.bedragCenten
  }
  return { internCenten, klantCenten }
}

/**
 * Het GEDEKTE deel van een regel in centen: intern + klant samen, begrensd op
 * het regelbedrag (een kost die zowel aan een voertuig als aan een klant hangt
 * telt één keer). Tekens volgen de regel (creditregels negatief). "Volledig
 * toegewezen" = gedekt === regelbedrag.
 */
export function toewijzingGedektCenten(regelCenten: number, internCenten: number, klantCenten: number): number {
  const som = internCenten + klantCenten
  if (regelCenten >= 0) return Math.max(0, Math.min(regelCenten, som))
  return Math.min(0, Math.max(regelCenten, som))
}

/**
 * Dubbel toegerekend (spec 183 §3.3): intern + klant samen is meer dan het
 * regelbedrag. Toegestaan, maar de UI waarschuwt; de waarde is het overlappende
 * deel in centen (0 = geen overlap).
 */
export function toewijzingOverlapCenten(regelCenten: number, internCenten: number, klantCenten: number): number {
  if (internCenten === 0 || klantCenten === 0) return 0
  const som = internCenten + klantCenten
  if (regelCenten >= 0) return som > regelCenten ? som - regelCenten : 0
  return som < regelCenten ? regelCenten - som : 0
}

/**
 * batch-66 voertuigcontrole (FO-489) — passen deze toewijzingen samen binnen het
 * regelbedrag (per dimensie: interne dragers en klant-toerekening elk)?
 *
 * Sinds de kentekenstap altijd draait kan een regel twee voorstellen dragen die
 * elkaars alternatief zijn (het besteltraject-doel én het voertuig, elk voor het
 * hele bedrag). "Alle suggesties accepteren" zou die allebei boeken en de kosten
 * dubbel tellen; met deze check slaat het accepteren zo'n regel over tot de
 * reviewer kiest. Zelfde regel als `refineToewijzingenBinnenRegel` bij opslaan.
 */
export function toewijzingenPassenBinnenRegel(
  regelCenten: number,
  toewijzingen: ReadonlyArray<{ doeltype: ToewijzingDoeltype | string; bedragCenten: number }>,
): boolean {
  const { internCenten, klantCenten } = toewijzingDimensiesCenten(toewijzingen)
  return (
    toewijzingOverschrijdingCenten(regelCenten, internCenten) === 0 &&
    toewijzingOverschrijdingCenten(regelCenten, klantCenten) === 0
  )
}

/** De uitleg bij een regel waar accepteren niet past (API en review-scherm). */
export const TOEWIJZING_KIES_VOORSTEL_MELDING =
  'Er staan meerdere voorstellen voor deze regel die samen meer zijn dan het regelbedrag. Kies welk voorstel geldt.'

/**
 * Inline toewijzen van één regel (werkvoorraad "Niet toegewezen" + accepteer-
 * flow): vervang de toewijzingen van de regel integraal. Een kostensoort is
 * verplicht zodra er een toewijzing is (FO-147 §6: zonder soort geen
 * definitieve toewijzing) — de refine dwingt dat af met een veldfout.
 * batch-65 (FO-482): de soort komt uit de stamtabel (`kostensoortId`).
 */
export const inkoopRegelToewijzingenUpdateSchema = z
  .object({
    toewijzingen: z.array(inkoopRegelToewijzingInputSchema).max(50),
    kostensoortId: idParam('kostensoortId').optional().nullable(),
  })
  .superRefine((data, ctx) => {
    // batch-65 (FO-483) — de soort is verplicht bij een INTERNE drager; een
    // klant-toerekening mag zonder (hij telt dan in de marge als "zonder
    // kostensoort" en staat in de werkvoorraad regels zonder kostensoort).
    if (data.toewijzingen.some((t) => isKostendragerDoeltype(t.doeltype)) && !data.kostensoortId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies een kostensoort om de toewijzing definitief te maken.',
        path: ['kostensoortId'],
      })
    }
    const keys = new Set<string>()
    for (const [i, t] of data.toewijzingen.entries()) {
      refineToewijzingDoel(t, ctx, ['toewijzingen', i])
      const key = toewijzingDoelKey(t)
      if (keys.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Elk doel mag maar één keer in de verdeling staan.',
          path: ['toewijzingen'],
        })
        break
      }
      keys.add(key)
    }
    // De som-tegen-regelbedrag-check kan hier niet (het regelbedrag zit niet in
    // de body); de route valideert dat server-side met dezelfde melding.
  })
export type InkoopRegelToewijzingenUpdateInput = z.infer<typeof inkoopRegelToewijzingenUpdateSchema>

// ─── Read-DTO's ──────────────────────────────────────────────────────────────

/** Eén toewijzing op een inkoopfactuurregel (read-vorm in de factuur-detail). */
export interface InkoopRegelToewijzingDTO {
  id: string
  /** FO-320 — doeltype + de gevulde doel-id (XOR). */
  doeltype: ToewijzingDoeltype
  employeeId: string | null
  voertuigId: string | null
  gereedschapsDragerId: string | null
  /** batch-65 (FO-483) — klant-toerekening (XOR met de drie hierboven). */
  clientId: string | null
  werkbonId: string | null
  projectId: string | null
  /**
   * batch-65 (FO-483) — de klant achter een KLANT-, WERKBON- of PROJECT-doel
   * (bij werkbon/project afgeleid), voor de weergave "Werkbon 2026-0417 · Pokon".
   * null bij de interne doeltypen.
   */
  klantId: string | null
  klantNaam: string | null
  /** Weergavenaam van het doel (medewerker / kenteken / drager / klant / werkbon- of projectnummer). */
  doelNaam: string | null
  /**
   * @deprecated FO-320 — gebruik `doelNaam`. Blijft gevuld voor MEDEWERKER-doelen
   * (backward-compat met de bestaande factuur-review-UI).
   */
  employeeNaam: string | null
  /** Aandeel-bedrag (excl. btw); negatief op creditregels. */
  bedrag: number
  bron: KostenToewijzingBron
  bronLabel: string
  status: KostenToewijzingStatus
  /** AI-bron: de herkende persoonsnaam (hint, ook zonder eenduidige match). */
  aiPersoonsnaam: string | null
  /** AI-bron: het herkende kenteken (hint bij voertuig-suggesties). */
  aiKenteken: string | null
  /**
   * FO-320 §6 — het voertuig-doel is niet (meer) actief (verkocht/afgeschreven):
   * de toewijzing blijft geldig (naboeking) maar met een waarschuwing die óók in
   * de historie zichtbaar blijft. Altijd false voor niet-voertuig-doelen.
   */
  voertuigInactief: boolean
  // FO-339 — context-koppeling (informatief, telt nooit mee). Een VOERTUIG-doel
  // toont de context-bestuurder, een MEDEWERKER-doel het context-voertuig.
  contextEmployeeId: string | null
  contextVoertuigId: string | null
  /** Weergavenaam van de context (bestuurder-naam / kenteken), of null. */
  contextNaam: string | null
}

/** Werkvoorraad-rij "Niet toegewezen": regel zonder (volledige) toewijzing. */
export interface NietToegewezenRegelDTO {
  regelId: string
  factuurId: string
  factuurNr: string
  leverancierId: string
  leverancierNaam: string
  entityId: string | null
  bvNaam: string | null
  /** Factuurdatum (yyyy-MM-dd, Europe/Amsterdam). */
  datum: string
  omschrijving: string
  /** batch-65 (FO-482) — kostensoort uit de stamtabel (null = nog niet gekozen). */
  kostensoortId: string | null
  /** Weergave "Hoofdgroep › Soort", of null. */
  kostensoortNaam: string | null
  /** Regelbedrag (excl. btw). */
  totaal: number
  /**
   * Gedekt deel: de DEFINITIEVE toewijzingen aan interne dragers en aan klanten
   * samen, begrensd op het regelbedrag (batch-65 FO-483: een kost op een
   * voertuig én een klant telt één keer).
   */
  toegewezen: number
  /** batch-65 (FO-483) — waarvan aan een klant/werkbon/project toegerekend. */
  klantToegerekend: number
  /** Nog niet toegewezen deel (totaal - toegewezen). */
  rest: number
  /** Aantal open suggesties op de regel. */
  suggesties: number
  /**
   * FO-320 — vermoedelijk doeltype o.b.v. de leveranciers-default (garage →
   * voertuig): voorselectie in de inline-toewijzen-UI + zichtbaar in de
   * werkvoorraad zodat materieel-kosten opvallen. null = geen leveranciers-hint.
   */
  vermoedelijkDoeltype: ToewijzingDoeltype | null
}

export interface NietToegewezenOverzichtDTO {
  regels: NietToegewezenRegelDTO[]
  totaalRest: number
  /** `afgekapt`: de scan raakte de server-cap — verfijn de filters (geen stille cap). */
  meta: { total: number; page: number; pageSize: number; afgekapt: boolean }
}

// ─── Suggestie-engine (on-demand run) ────────────────────────────────────────

/** Wat de suggestie-engine per regel heeft gedaan (respons van de on-demand run). */
export interface RegelSuggestieResultaatDTO {
  regelId: string
  bron: KostenToewijzingBron | null
  toewijzingen: number
  directDefinitief: boolean
  aiHint: string | null
  /** FO-320 — herkend kenteken zonder eenduidige actieve-voertuig-suggestie. */
  kentekenHint: string | null
  /** FO-320 — de voertuig-suggestie betreft een niet-(meer-)actief voertuig. */
  voertuigInactief: boolean
  /** batch-65 (FO-482) — stamtabel-code van de gezette kostensoort (alleen als die leeg was). */
  kostensoortGezet: string | null
  /** batch-65 (FO-482) — waar die soort vandaan kwam. */
  kostensoortBron: string | null
}

export interface SuggestieRunResultaatDTO {
  factuurId: string
  regels: RegelSuggestieResultaatDTO[]
  aantalToewijzingen: number
}

// ─── Instellingen (singleton) ────────────────────────────────────────────────

export const inkoopToewijzingInstellingenUpdateSchema = z.object({
  besteltrajectDirectDefinitief: z.boolean().optional(),
  // FO-393 (batch-46) — 3-way-match-toleranties + ontvangst-zonder-factuur-horizon.
  matchTolerantiePct: z.number().min(0).max(100).optional(),
  matchTolerantieAbsoluut: z.number().min(0).optional(),
  ontvangstZonderFactuurDagen: z.number().int().min(1).max(365).optional(),
  // batch-65 inkoop-kosten (FO-483) — doorbelasten: globale opslag als FRACTIE
  // (0.1 = 10%, de terugval als de klant geen eigen opslag heeft) en de ouderdom
  // in dagen waarna een te doorbelasten klantregel een signaal geeft.
  doorbelastOpslagPctDefault: z.number().finite().min(0).max(9.99, 'De opslag kan hoogstens 999% zijn.').optional(),
  doorbelastOuderdomDagen: z.number().int().min(1).max(365).optional(),
})
export type InkoopToewijzingInstellingenUpdateInput = z.infer<
  typeof inkoopToewijzingInstellingenUpdateSchema
>

export interface InkoopToewijzingInstellingenDTO {
  besteltrajectDirectDefinitief: boolean
  // FO-393 (batch-46) — 3-way-match-instellingen.
  matchTolerantiePct: number
  matchTolerantieAbsoluut: number
  ontvangstZonderFactuurDagen: number
  /** batch-65 (FO-483) — globale doorbelast-opslag (fractie) + ouderdomstermijn. */
  doorbelastOpslagPctDefault: number
  doorbelastOuderdomDagen: number
}

// ─── Organisatiebrede kosten-trends (FO-147 §5) ──────────────────────────────

export interface OrgKostenTrendsTopMedewerkerDTO {
  employeeId: string
  naam: string
  functie: string | null
  totaal: number
  perSoort: Record<string, number>
}

export interface OrgKostenTrendsTopLeverancierDTO {
  leverancierId: string
  naam: string
  totaal: number
  /** Aantal kosten-posten (toewijzing-aandelen/maandlasten), niet unieke regels. */
  aantalPosten: number
}

export interface OrgKostenTrendsUitschieterDTO {
  employeeId: string
  naam: string
  functie: string
  totaal: number
  functieGemiddelde: number
  /** totaal / functieGemiddelde (>= de signaaldrempel). */
  factor: number
  aantalFunctiegenoten: number
}

export interface OrgKostenTrendsJaarOpJaarDTO {
  soort: string
  soortLabel: string
  huidig: number
  vorigJaar: number
  /** Percentuele verandering t.o.v. vorig jaar; null wanneer vorig jaar 0 was. */
  deltaPct: number | null
}

/** Dekking-splitsing per doeltype (FO-320 §2.5). */
export interface OrgKostenTrendsDekkingPerDoeltypeDTO {
  doeltype: ToewijzingDoeltype
  /** Som van de DEFINITIEVE toewijzingen naar dit doeltype (excl. btw). */
  toegewezen: number
  /** Aantal regels met minstens één toewijzing naar dit doeltype. */
  aantalRegels: number
}

export interface OrgKostenTrendsDekkingDTO {
  /** Som van alle inkoopfactuurregels (excl. btw) in de periode/filters. */
  totaalInkoop: number
  /**
   * Gedekte som op die regels: de DEFINITIEVE toewijzingen (intern en klant),
   * per regel begrensd op het regelbedrag (batch-65 FO-483, geen dubbeltelling).
   */
  toegewezen: number
  /** toegewezen / totaalInkoop × 100 (0 wanneer er geen inkoop is). */
  dekkingPct: number
  /** Aantal regels zonder volledige definitieve toewijzing (werkvoorraad). */
  aantalOnvolledig: number
  /**
   * FO-320 — de toegewezen som uitgesplitst naar dragertype (medewerkers /
   * voertuigen / karren) zodat zichtbaar is of ook materieel-kosten netjes
   * worden toegewezen. Altijd alle drie de doeltypen aanwezig (0 waar niets);
   * batch-65 (FO-483): daarna de klant-toerekening (klanten / werkbonnen /
   * projecten), ook altijd aanwezig.
   */
  perDoeltype: OrgKostenTrendsDekkingPerDoeltypeDTO[]
}

export interface OrgKostenTrendsKalibratieDTO {
  soort: string
  soortLabel: string
  /** Werkelijke kosten per fte per maand (actuals / maanden / fte-totaal). */
  actueelPerFteMaand: number
  /** Parametrische aanname per fte per maand uit EmployeeCostProfile; null
   *  wanneer er geen profiel-veld op deze soort mapt. */
  aannamePerFteMaand: number | null
}

export interface OrgKostenTrendsDTO {
  granulariteit: TrendGranulariteit
  van: string | null
  tot: string | null
  soorten: { soort: string; soortLabel: string }[]
  /** Buckets periode × soort voor de gestapelde grafiek. */
  periodes: TrendPeriodeDTO[]
  /** Zelfde buckets één jaar eerder (jaar-op-jaar-vergelijking in de grafiek). */
  vorigJaarPeriodes: TrendPeriodeDTO[]
  jaarOpJaar: OrgKostenTrendsJaarOpJaarDTO[]
  topMedewerkers: OrgKostenTrendsTopMedewerkerDTO[]
  topLeveranciers: OrgKostenTrendsTopLeverancierDTO[]
  uitschieters: OrgKostenTrendsUitschieterDTO[]
  dekking: OrgKostenTrendsDekkingDTO
  kalibratie: OrgKostenTrendsKalibratieDTO[]
  /** fte-totaal + maanden waarover de kalibratie is genormaliseerd. */
  kalibratieBasis: { fteTotaal: number; maanden: number; aantalMedewerkers: number }
}
